import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { AuthUser, UserRole } from '@recruitment/types';
import {
  registerUnauthorizedHandler,
  setSession,
  clearSession,
  getAccessToken,
  clearStoredRefreshTokens,
  beginLogout,
} from '@/lib/session';
import { API_BASE } from '@/lib/api-client';
import { initiateMicrosoftSSO } from '@/lib/auth-helpers';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  hasPanelRole: () => boolean;
  signIn: (staffId: string, password: string) => Promise<void>;
  signInWithEmail: (
    email: string,
    password: string,
    otp?: string,
  ) => Promise<void>;
  loginWithToken: (accessToken: string, refreshToken?: string) => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ROLE_EXPANSIONS: Record<UserRole, UserRole[]> = {
  candidate: ['candidate'],
  external_candidate: ['external_candidate'],
  super_admin: ['super_admin', 'superadmin', 'admin', 'system_admin'],
  system_admin: ['system_admin', 'admin'],
  hcm_admin: ['hcm_admin', 'hr'],
  hcm_reviewer: ['hcm_reviewer'],
  assessment_review_approver: ['assessment_review_approver'],
  hcm_support: ['hcm_support'],
  panel_member: ['panel_member', 'interviewer'],
  panel_secretary: ['panel_secretary', 'secretary'],
  candidate_verifier: ['candidate_verifier'],
  qa_qc: ['qa_qc'],
  management: ['management'],
  superadmin: ['superadmin', 'super_admin', 'admin', 'system_admin'],
  admin: ['admin', 'system_admin'],
  hr: ['hr', 'hcm_admin'],
  interviewer: ['interviewer', 'panel_member'],
  secretary: ['secretary', 'panel_secretary'],
  screener: ['screener', 'hcm_admin'],
};

const INTERNAL_PORTAL_ROLES = new Set<UserRole>([
  'super_admin',
  'system_admin',
  'hcm_admin',
  'hcm_reviewer',
  'assessment_review_approver',
  'hcm_support',
  'panel_member',
  'panel_secretary',
  'candidate_verifier',
  'qa_qc',
  'management',
  'superadmin',
  'admin',
  'hr',
  'interviewer',
  'secretary',
  'screener',
]);

function canUseInternalPortal(user: AuthUser): boolean {
  if (INTERNAL_PORTAL_ROLES.has(user.role)) return true;
  if (
    (user.roles ?? []).some((role) =>
      INTERNAL_PORTAL_ROLES.has(role as UserRole),
    )
  )
    return true;
  return (user.panelRoles ?? []).length > 0;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on mount via silent refresh — refresh token lives in httpOnly cookie.
  // Use raw fetch here (not apiRequest) to avoid triggering the unauthorized handler
  // when there is simply no active session (e.g. fresh page load, no cookie).
  useEffect(() => {
    (async () => {
      try {
        clearStoredRefreshTokens();
        const refreshRes = await fetch(
          `${API_BASE}/api/auth/refresh/internal`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          },
        );
        if (!refreshRes.ok) return;
        const tokens = (await refreshRes.json()) as {
          accessToken?: string;
          refreshToken?: string;
        };
        if (!tokens.accessToken) return;
        setSession({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });

        const meRes = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
        });
        if (meRes.ok) {
          const profile = (await meRes.json()) as AuthUser;
          if (canUseInternalPortal(profile)) {
            setUser(profile);
          } else {
            clearSession();
          }
        }
      } catch {
        // No session — stay logged out; router will redirect to /login
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const hasRole = (...roles: UserRole[]) => {
    if (!user) return false;
    const expanded = new Set<string>(ROLE_EXPANSIONS[user.role] ?? [user.role]);
    for (const assigned of user.roles ?? []) {
      expanded.add(assigned);
      const aliases = ROLE_EXPANSIONS[assigned as UserRole];
      if (aliases) {
        for (const alias of aliases) expanded.add(alias);
      }
      if (assigned.startsWith('panel_')) {
        expanded.add('panel_member');
      }
    }
    for (const panelRole of user.panelRoles ?? []) {
      expanded.add(panelRole);
      if (panelRole.startsWith('panel_')) expanded.add('panel_member');
    }
    return roles.some((role) => expanded.has(role));
  };

  const hasPanelRole = () => {
    if (!user) return false;
    if ((user.panelRoles?.length ?? 0) > 0) return true;
    if ((user.roles ?? []).some((role) => role.startsWith('panel_')))
      return true;
    return hasRole(
      'panel_member',
      'panel_secretary',
      'interviewer',
      'secretary',
    );
  };

  const loginWithToken = async (accessToken: string, refreshToken?: string) => {
    setSession({ accessToken, refreshToken });
    const meResponse = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!meResponse.ok) throw new Error('Failed to fetch profile');
    const profile = (await meResponse.json()) as AuthUser;
    if (!canUseInternalPortal(profile)) {
      clearSession();
      throw new Error('This account is not allowed to use the internal portal');
    }
    setUser(profile);
    setIsLoading(false);
  };

  const signInWithMicrosoft = async () => {
    await initiateMicrosoftSSO();
    // Page will redirect; no return
  };

  const signIn = async (staffId: string, password: string) => {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ staffId, password }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message ?? 'Invalid staff ID or password');
    }

    const tokenPair = await response.json();
    setSession({
      accessToken: tokenPair.accessToken,
      refreshToken: tokenPair.refreshToken,
    });

    const meResponse = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${tokenPair.accessToken}` },
    });
    if (!meResponse.ok)
      throw new Error('Login succeeded but failed to fetch profile');

    const profile = (await meResponse.json()) as AuthUser;
    if (!canUseInternalPortal(profile)) {
      clearSession();
      throw new Error('This account is not allowed to use the internal portal');
    }
    setUser(profile);
    setIsLoading(false);
  };

  const signInWithEmail = async (
    email: string,
    password: string,
    otp?: string,
  ) => {
    const response = await fetch(`${API_BASE}/api/auth/admin/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, otp }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message ?? 'Invalid email or password');
    }
    const tokenPair = await response.json();
    setSession({
      accessToken: tokenPair.accessToken,
      refreshToken: tokenPair.refreshToken,
    });
    const meResponse = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${tokenPair.accessToken}` },
    });
    if (!meResponse.ok)
      throw new Error('Login succeeded but failed to fetch profile');
    const profile = (await meResponse.json()) as AuthUser;
    if (!canUseInternalPortal(profile)) {
      clearSession();
      throw new Error('This account is not allowed to use the internal portal');
    }
    setUser(profile);
    setIsLoading(false);
  };

  const logout = useCallback(() => {
    const token = getAccessToken();
    // Intentional logout: drop any stored return path and stop a new one from
    // being captured by the brief `user === null` re-render before we redirect.
    // The user should land on their default route after signing back in.
    beginLogout();
    const doLogout = async () => {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch {
        // best-effort — proceed with local cleanup regardless
      } finally {
        clearSession();
        setUser(null);
        window.location.href = '/login';
      }
    };
    void doLogout();
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(() => logout());
    return () => registerUnauthorizedHandler(null);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        hasRole,
        hasPanelRole,
        signIn,
        signInWithEmail,
        loginWithToken,
        signInWithMicrosoft,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
